import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowsRightLeftIcon, FolderPlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import FolderDrawerTreeView from '../FolderDrawerTreeView';
import {
  getExecutionFileFolders,
  createExecutionFileFolder,
  updateExecutionFileFolder,
  deleteExecutionFileFolder,
  moveExecutionFileFolder,
} from '../../utils/api/executionFileFoldersApis';
import {
  getExecutionFiles,
  getExecutionFilesAll,
  updateExecutionFile,
  deleteExecutionFile,
  moveExecutionFile,
  saveExecutionFile,
} from '../../utils/api/executionFileApis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { transformFolders, findFolderById } from './helpers';
import FileListPanel from './FileListPanel';
import FolderPopups from './FolderPopups';
import MoveFileModal from './MoveFileModal';
import UploadFileModal from './UploadFileModal';
import ScanningFilesMonitor from './ScanningFilesMonitor';
import QuickSearch from './QuickSearch';
import styles from '../../styles/assetsManagement/filesManager.module.scss';


const FilesManager = () => {
  const [rawFolders, setRawFolders] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [popup, setPopup] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingUploadFileId, setPendingUploadFileId] = useState(null);

  const { showSnackbar } = useSnackbar();
  const treeRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const res = await getExecutionFileFolders();
      if (res.status === STATUS.SUCCESS) {
        setRawFolders(res.data);
        const transformed = transformFolders(res.data);
        setTreeData(transformed);
        if (transformed.length > 0) {
          treeRef.current?.triggerExpandAll(transformed);
        }
      } else {
        showSnackbar(res.error || 'Failed to load folders', 'error');
      }
    } finally {
      setLoadingFolders(false);
    }
  }, []); // intentionally empty – runs once on mount

  const openPopup = useCallback((type, data = null) => { setPopup(type); setPopupData(data); }, []);
  const closePopup = useCallback(() => { setPopup(null); setPopupData(null); setActionLoading(false); }, []);

  const finalizeQuarantineCleanFiles = useCallback(async (loadedFiles, folderIdForFallback) => {
    const toFinalize = (loadedFiles || []).filter(
      (f) =>
        f?.scannedStatus === 'CLEAN' &&
        typeof f.fileKey === 'string' &&
        f.fileKey.includes('/quarantine/'),
    );
    if (!toFinalize.length) return { attempted: 0, failed: 0 };

    let failed = 0;
    await Promise.all(
      toFinalize.map(async (f) => {
        try {
          const res = await saveExecutionFile({
            executionFileId: f.id,
            tempFileKey: f.fileKey,
            folderId: f.folderId || folderIdForFallback,
            fileName: f.fileName,
            fileDescription: f.fileDescription || undefined,
            fileMetadata: f.fileMetadata || undefined,
          });
          if (res.status !== STATUS.SUCCESS) failed += 1;
        } catch {
          failed += 1;
        }
      }),
    );

    return { attempted: toFinalize.length, failed };
  }, []);

  const loadFiles = useCallback(async (folderId) => {
    if (!folderId) return;
    setLoadingFiles(true);
    try {
      const res = await getExecutionFiles({ folderId });
      if (res.status === STATUS.SUCCESS) {
        const loaded = res.data?.files || [];
        setFiles(loaded);
        const finalizeRes = await finalizeQuarantineCleanFiles(loaded, folderId);
        if (finalizeRes.attempted) {
          if (finalizeRes.failed) {
            showSnackbar('Some files could not be finalized after scan. Please refresh and try again.', 'warning');
          }
          const refreshed = await getExecutionFiles({ folderId });
          if (refreshed.status === STATUS.SUCCESS) setFiles(refreshed.data?.files || []);
        }
      } else {
        showSnackbar(res.error || 'Failed to load files', 'error');
        setFiles([]);
      }
    } catch {
      showSnackbar('Unexpected error loading files', 'error');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [showSnackbar, finalizeQuarantineCleanFiles]);

  const loadAllFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const res = await getExecutionFilesAll();
      if (res.status === STATUS.SUCCESS) {
        const loaded = res.data?.files || [];
        setFiles(loaded);
        const finalizeRes = await finalizeQuarantineCleanFiles(loaded, null);
        if (finalizeRes.attempted) {
          if (finalizeRes.failed) {
            showSnackbar('Some files could not be finalized after scan. Please refresh and try again.', 'warning');
          }
          const refreshed = await getExecutionFilesAll();
          if (refreshed.status === STATUS.SUCCESS) setFiles(refreshed.data?.files || []);
        }
      } else {
        showSnackbar(res.error || 'Failed to load files', 'error');
        setFiles([]);
      }
    } catch {
      showSnackbar('Unexpected error loading files', 'error');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [showSnackbar, finalizeQuarantineCleanFiles]);

  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    loadFolders();
    loadAllFiles();
    return () => {
      initialLoadDoneRef.current = false;
    };
  }, []);

  const handleFolderSelect = useCallback((folderId) => {
    if (folderId === 'default') {
      setSelectedFolderId(null);
      setSelectedFolder(null);
      loadAllFiles();
      return;
    }
    setSelectedFolderId(folderId);
    const folder = findFolderById(rawFolders, folderId);
    setSelectedFolder(folder || null);
    loadFiles(folderId);
  }, [rawFolders, loadFiles, loadAllFiles]);

  useEffect(() => {
    if (selectedFolderId) {
      setSelectedFolder(findFolderById(rawFolders, selectedFolderId) || null);
    }
  }, [rawFolders, selectedFolderId]);

  const handleCreateFolder = useCallback(async ({ folderName, folderDescription, parentFolderId }) => {
    setActionLoading(true);
    try {
      const res = await createExecutionFileFolder({ folderName, folderDescription, parentFolderId });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar('Folder created successfully', 'success');
        await loadFolders();
        closePopup();
      } else {
        showSnackbar(res.error || 'Failed to create folder', 'error');
        setActionLoading(false);
      }
    } catch {
      showSnackbar('Unexpected error', 'error');
      setActionLoading(false);
    }
  }, [loadFolders, closePopup, showSnackbar]);

  const handleRenameFolder = useCallback(async ({ folderName, folderDescription }) => {
    if (!popupData?.id) return;
    setActionLoading(true);
    try {
      const res = await updateExecutionFileFolder({ folderId: popupData.id, folderName, folderDescription });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar('Folder updated successfully', 'success');
        await loadFolders();
        closePopup();
      } else {
        showSnackbar(res.error || 'Failed to update folder', 'error');
        setActionLoading(false);
      }
    } catch {
      showSnackbar('Unexpected error', 'error');
      setActionLoading(false);
    }
  }, [popupData, loadFolders, closePopup, showSnackbar]);

  const handleDeleteFolder = useCallback(async () => {
    if (!popupData?.id) return;
    const deletedFolderId = popupData.id;
    const wasInsideDeletedFolder = selectedFolderId === deletedFolderId;
    const wasViewingAllFiles = selectedFolderId === null;
    setActionLoading(true);
    try {
      const res = await deleteExecutionFileFolder({ folderId: deletedFolderId });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar(res.message || 'Folder deleted', 'success');
        if (wasInsideDeletedFolder) {
          setSelectedFolderId(null);
          setSelectedFolder(null);
        }
        await loadFolders();
        if (wasInsideDeletedFolder || wasViewingAllFiles) {
          await loadAllFiles();
        }
        closePopup();
      } else {
        showSnackbar(res.error || 'Failed to delete folder', 'error');
        setActionLoading(false);
      }
    } catch {
      showSnackbar('Unexpected error', 'error');
      setActionLoading(false);
    }
  }, [popupData, selectedFolderId, loadFolders, loadAllFiles, closePopup, showSnackbar]);

  const handleMoveFolder = useCallback(async (newParentFolderId) => {
    if (!popupData?.id) return;
    setActionLoading(true);
    try {
      const res = await moveExecutionFileFolder({ folderId: popupData.id, parentFolderId: newParentFolderId });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar('Folder moved successfully', 'success');
        await loadFolders();
        closePopup();
      } else {
        showSnackbar(res.error || 'Failed to move folder', 'error');
        setActionLoading(false);
      }
    } catch {
      showSnackbar('Unexpected error', 'error');
      setActionLoading(false);
    }
  }, [popupData, loadFolders, closePopup, showSnackbar]);

  const handleEditFile = useCallback(async (fileId, updates) => {
    try {
      const res = await updateExecutionFile({
        executionFileId: fileId,
        fileName: updates.fileName,
        fileDescription: updates.fileDescription,
      });
      if (res.status === STATUS.SUCCESS) {
        const data = res.data || {};
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  fileName: data.fileName ?? f.fileName,
                  fileDescription: data.fileDescription !== undefined ? data.fileDescription : f.fileDescription,
                }
              : f,
          ),
        );
        showSnackbar('File updated successfully', 'success');
      } else {
        showSnackbar(res.error || 'Failed to update file', 'error');
      }
    } catch {
      showSnackbar('Unexpected error updating file', 'error');
    }
  }, [showSnackbar]);

  const handleDeleteFile = useCallback(async (fileId) => {
    try {
      const res = await deleteExecutionFile({ executionFileId: fileId });
      if (res.status === STATUS.SUCCESS) {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
        showSnackbar('File deleted successfully', 'success');
        if (selectedFolderId) loadFiles(selectedFolderId);
        else loadAllFiles();
        await loadFolders();
      } else {
        showSnackbar(res.error || 'Failed to delete file', 'error');
      }
    } catch {
      showSnackbar('Unexpected error deleting file', 'error');
    }
  }, [showSnackbar, loadFolders, selectedFolderId, loadFiles, loadAllFiles]);

  const handleMoveFileClick = useCallback((file) => {
    openPopup('move_file', file);
  }, [openPopup]);

  const handleMoveFile = useCallback(async (targetFolderId) => {
    if (!popupData?.id) return;
    setActionLoading(true);
    try {
      const res = await moveExecutionFile({
        executionFileId: popupData.id,
        targetFolderId,
      });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar(res.data?.moved ? 'File moved successfully' : (res.data?.message || 'File moved'), 'success');
        if (selectedFolderId) loadFiles(selectedFolderId);
        else loadAllFiles();
        await loadFolders();
        closePopup();
      } else {
        showSnackbar(res.error || 'Failed to move file', 'error');
      }
    } catch {
      showSnackbar('Unexpected error moving file', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [popupData, selectedFolderId, loadFiles, loadFolders, closePopup, showSnackbar]);

  const handleUploadComplete = useCallback((fileData, options = {}) => {
    const { closeModal = true } = options || {};
    if (!closeModal && fileData?.id) {
      setPendingUploadFileId(fileData.id);
      setFiles((prev) => (prev.some((f) => f.id === fileData.id) ? prev : [fileData, ...prev]));
    }
    if (closeModal) closePopup();
    showSnackbar(
      fileData.duplicate
        ? 'File already exists in this folder (linked to existing)'
        : 'File uploaded successfully',
      fileData.duplicate ? 'warning' : 'success',
    );
    if (selectedFolderId) loadFiles(selectedFolderId);
    else loadAllFiles();
    loadFolders();
  }, [closePopup, showSnackbar, selectedFolderId, loadFiles, loadAllFiles, loadFolders]);

  const handleScanStatusUpdate = useCallback((fileId, updates) => {
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f)));
  }, []);

  const folderRowActions = useCallback((node) => {
    const items = [];
    if (node.level < 2) {
      items.push({
        name: 'New Subfolder',
        icon: <FolderPlusIcon className="icon-small" />,
        action: () => openPopup('create_folder', { parentFolderId: node.id, level: node.level + 1 }),
      });
    }
    items.push({
      name: 'Rename',
      icon: <PencilIcon className="icon-small" />,
      action: () => openPopup('rename_folder', node),
    });
    items.push({
      name: 'Move',
      icon: <ArrowsRightLeftIcon className="icon-small" />,
      action: () => openPopup('move_folder', node),
    });
    items.push({
      name: 'Delete',
      icon: <TrashIcon className="icon-small" />,
      action: () => openPopup('delete_folder', node),
    });
    return items;
  }, [openPopup]);

  const treeHeaderActions = useMemo(() => [
    {
      name: 'New Folder',
      icon: <FolderPlusIcon className="icon-small" />,
      action: () => openPopup('create_folder', { parentFolderId: null, level: 0 }),
    },
  ], [openPopup]);

  return (
    <div className={styles.managerWrap}>
      {/* <QuickSearch /> */}

      <div className={styles.container}>
      <div className={styles.folderPanel}>
        {loadingFolders && treeData.length === 0 ? (
          <div className={styles.spinnerCenter}>
            <span className={styles.spinner} aria-label="Loading folders" />
          </div>
        ) : (
          <FolderDrawerTreeView
            ref={treeRef}
            data={treeData}
            title="Folders"
            moduleTitle="All Files"
            onLeafNodeClick={handleFolderSelect}
            rowActionButtons={folderRowActions}
            disableActions={false}
            disableHeaderActions={false}
            headerActionButtons={treeHeaderActions}
            selected={selectedFolderId ? [String(selectedFolderId)] : ['default']}
            setSelected={() => {}}
            showSearch={treeData.length > 3}
          />
        )}
      </div>

      <div className={styles.divider} role="separator" aria-orientation="vertical" />

      <FileListPanel
        folder={selectedFolder}
        files={files}
        loadingFiles={loadingFiles}
        onUpload={() => openPopup('upload_file')}
        onEditFile={handleEditFile}
        onDeleteFile={handleDeleteFile}
        onMoveFile={handleMoveFileClick}
        allFilesView={selectedFolderId === null}
      />

      <FolderPopups
        popup={popup}
        popupData={popupData}
        rawFolders={rawFolders}
        actionLoading={actionLoading}
        onClose={closePopup}
        onCreateFolder={handleCreateFolder}
        onRenameFolder={handleRenameFolder}
        onDeleteFolder={handleDeleteFolder}
        onMoveFolder={handleMoveFolder}
      />

      <ScanningFilesMonitor
        files={files}
        selectedFolderId={selectedFolderId}
        onUpdate={handleScanStatusUpdate}
      />

      {popup === 'upload_file' && selectedFolder && (
        <UploadFileModal
          folder={selectedFolder}
          fileScanStatus={
            pendingUploadFileId
              ? files.find((f) => f.id === pendingUploadFileId)?.scannedStatus
              : null
          }
          onUploadComplete={handleUploadComplete}
          onClose={() => {
            setPendingUploadFileId(null);
            closePopup();
          }}
        />
      )}

      {popup === 'move_file' && popupData && (
        <MoveFileModal
          file={popupData}
          currentFolderId={selectedFolderId}
          rawFolders={rawFolders}
          onConfirm={handleMoveFile}
          onClose={closePopup}
          loading={actionLoading}
        />
      )}
      </div>
    </div>
  );
};

export default FilesManager;
